// TodoBoard.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Box, Paper, Typography, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Tooltip, MenuItem, LinearProgress, Checkbox, FormControlLabel
} from "@mui/material";
import { Add, Delete, Edit, AttachFile, CalendarMonth, Label } from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { db, storage } from "./firebase";
import {
  collection, query, where, orderBy, onSnapshot
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { apiFetch, API_BASE } from "./utils/apiClient";
import UploadPreview from "./components/UploadPreview";

// الأعمدة (مثل تريللو)
const COLUMNS = [
  { id: "backlog", title: " تجميع الافكار "},
  { id: "todo", title: " للإنجاز" },
  { id: "inprogress", title: " قيد التنفيذ" },
  { id: "done", title: " منجز" },
];


// ألوان اللابلز الجاهزة
const LABELS = [
  { id: "urgent", text: "عاجل", color: "#d32f2f" },
  { id: "review", text: "مراجعة", color: "#0288d1" },
  { id: "low", text: "خفيف", color: "#2e7d32" },
  { id: "idea", text: "فكرة", color: "#6a1b9a" },
];


export default function TodoBoard() {
  const [cards, setCards] = useState([]);         // كل الكروت (دوكومنتس)
  const [loading, setLoading] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);
  const [editing, setEditing] = useState(null);   // card object أو null
  const [uploading, setUploading] = useState(false);
  const ownerId = localStorage.getItem("username") || "anonymous"; // يعزل بيانات كل موظف

const navigate = useNavigate();

  // جلب كروت المستخدم مرتبة حسب العمود والـ order
  const fetchCards = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/todo-cards?ownerId=${encodeURIComponent(ownerId)}`);
      const data = await res.json();

      if (!res.ok) {
        console.error("Error fetching todo cards:", data.error);
        return;
      }

      // ترتيب حسب order (نفس ترتيب orderBy("status"), orderBy("order") الأصلي
      // بصرياً - التجميع حسب العمود يصير لاحقاً بـcolumns، فيكفي ترتيب واحد هنا)
      data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setCards(data);
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  if (!ownerId) return;

  const q = query(
    collection(db, "todoCards"),
    where("ownerId", "==", ownerId),
    orderBy("status"),
    orderBy("order")
  );

  const unsubscribe = onSnapshot(q, (snap) => {
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setCards(data);
  });

  return () => unsubscribe();
}, [ownerId]);   // ✅ يعتمد على ownerId

  // تحويل الكروت إلى خريطة أعمدة
  const columns = useMemo(() => {
    const map = {};
    COLUMNS.forEach(c => (map[c.id] = []));
    for (const c of cards) map[c.status]?.push(c);
    return map;
  }, [cards]);

  // فتح محرّر إضافة
  const openNew = () => {
    setEditing({
      title: "",
      description: "",
      status: "todo",
      dueDate: "",
      labels: [],
      checklist: [], // [{text, done}]
      attachments: [], // [{name,url}]
      order: (columns["todo"]?.length || 0),
    });
    setOpenEditor(true);
  };

  // حفظ (إضافة/تعديل)
  const saveCard = async () => {
    if (!editing.title.trim()) return;

    const basePayload = {
      title: editing.title,
      description: editing.description,
      status: editing.status,
      dueDate: editing.dueDate,
      labels: editing.labels,
      checklist: editing.checklist,
      attachments: editing.attachments,
      order: editing.order,
      ownerId,
    };

    setUploading(true);
    try {
      let res;

      if (editing.id) {
        res = await apiFetch(`${API_BASE}/todo-cards/${editing.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...basePayload,
            // نحافظ على تاريخ الإنشاء الأصلي عند التعديل، نفس الكود القديم بالضبط
            ...(editing.createdAt ? { createdAt: editing.createdAt } : {}),
          }),
        });
      } else {
        res = await apiFetch(`${API_BASE}/todo-cards`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(basePayload),
        });
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "فشل حفظ المهمة");
      }

      setOpenEditor(false);
      setEditing(null);
      fetchCards();
    } finally {
      setUploading(false);
    }
  };

  // حذف كرت
  const deleteCard = async (id) => {
    await apiFetch(`${API_BASE}/todo-cards/${id}`, { method: "DELETE" });
    fetchCards();
  };

  // رفع مرفقات
  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fRef = ref(storage, `todos/${ownerId}/${Date.now()}-${f.name}`);
        await uploadBytes(fRef, f);
        const url = await getDownloadURL(fRef);
        uploaded.push({ name: f.name, url });
      }
      setEditing((e) => ({ ...e, attachments: [...(e.attachments || []), ...uploaded] }));
    } finally {
      setUploading(false);
    }
  };

  // سحب وإفلات
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId &&
        source.index === destination.index) return;

    // تعديل محلي
    const moved = cards.find(c => c.id === draggableId);
    const newStatus = destination.droppableId;

    // نحدّث order في العمودين (مصدر/وجهة)
    const newCards = [...cards];
    // أخرج من العمود القديم
    const srcList = newCards
      .filter(c => c.status === source.droppableId && c.id !== draggableId)
      .sort((a,b)=>a.order-b.order);
    // أدخل في العمود الجديد في الموضع الجديد
    const dstList = newCards
      .filter(c => c.status === newStatus && c.id !== draggableId)
      .sort((a,b)=>a.order-b.order);

    dstList.splice(destination.index, 0, { ...moved, status: newStatus });

    // أعد تجميع بقية الأعمدة
    const rebuilt = newCards
      .filter(c => c.id !== draggableId && c.status !== newStatus && c.status !== source.droppableId);

    const all = [
      ...rebuilt,
      ...srcList.map((c, i) => ({ ...c, order: i })),
      ...dstList.map((c, i) => ({ ...c, order: i })),
    ];

    setCards(all);

    // حفظ دفعة واحدة عبر الباك اند (نفس writeBatch الأصلي بالضبط)
    await apiFetch(`${API_BASE}/todo-cards/reorder`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        updates: all.map((c) => ({ id: c.id, status: c.status, order: c.order })),
      }),
    });
  };

  // مكوّن بطاقة
  const Card = ({ card, index }) => {
    const checklistDone = card.checklist?.filter(x => x.done).length || 0;
    const checklistTotal = card.checklist?.length || 0;
    const overdue = card.dueDate && new Date(card.dueDate) < new Date() && card.status !== "done";

    return (
      <Draggable draggableId={card.id} index={index}>
        {(provided) => (
          <Paper
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            sx={{ p: 1.5, mb: 1.5, borderRadius: 2, display: "grid", gap: 1 }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography fontWeight={700} sx={{ mr: 1, wordBreak: "break-word" }}>{card.title}</Typography>
              <Box>
                <Tooltip title="تعديل">
                  <IconButton size="small" onClick={() => { setEditing(card); setOpenEditor(true); }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="حذف">
                  <IconButton size="small" color="error" onClick={() => deleteCard(card.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {card.labels?.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {card.labels.map(lid => {
                  const l = LABELS.find(x => x.id === lid);
                  if (!l) return null;
                  return <Chip key={lid} label={l.text} size="small" sx={{ bgcolor: l.color, color: "#fff" }} />;
                })}
              </Box>
            )}

            {card.description && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {card.description}
              </Typography>
            )}

            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              {card.dueDate && (
                <Chip
                  size="small"
                  icon={<CalendarMonth />}
                  label={new Date(card.dueDate).toLocaleDateString("ar-IQ")}
                  color={overdue ? "error" : "default"}
                  variant={overdue ? "filled" : "outlined"}
                />
              )}
              {checklistTotal > 0 && (
                <Chip
                  size="small"
                  label={`${checklistDone}/${checklistTotal}`}
                  icon={<Checkbox checked size="small" sx={{ p: 0.3 }} />}
                  variant="outlined"
                />
              )}
              {card.attachments?.length > 0 && (
                <Chip size="small" icon={<AttachFile />} label={`${card.attachments.length} ملف`} />
              )}
            </Box>
          </Paper>
        )}
      </Draggable>
    );
  };

  return (
    <Container dir="rtl" sx={{ mt: 3 }}>
      {/* 🔹 القسم العلوي: Home + العنوان + إضافة تاسك */}
<Box display="flex" flexDirection="column" alignItems="flex-start" mb={3}>
  {/* زر Home */}
 {/* زر Home */}

{(() => {
  const getHomePath = () => {
    switch (ownerId) {
      case "rand@123": return "/rand";
      case "jumana@123": return "/jumana";
      case "sara@123": return "/sara";
      case "Baneen@123": return "/Baneen";
      case "designer@123": return "/yusuf";   // المصمم
      case "art@1234": return "/burhan";
      case "karina@123": return "/karina";
      case "admin": return "/admin";
      default: return "/";
    }
  };

  return (
    <Button
      variant="outlined"
      color="primary"
      sx={{ mb: 1 }}
      onClick={() => navigate(getHomePath())}
    >
      🏠 Home
    </Button>
  );
})()}


  {/* العنوان */}
  <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
    📋 لوحة المهام (To-Do) — خاصة بك
  </Typography>

  {/* زر إضافة مهمة */}
  <Button
    variant="contained"
    color="success"
    onClick={openNew}
    startIcon={<Add />}
  >
    ➕ إضافة تاسك
  </Button>
</Box>


      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <DragDropContext onDragEnd={onDragEnd}>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
          gap: 2
        }}>
          {COLUMNS.map(col => (
            <Paper key={col.id} sx={{ p: 1.5, minHeight: 200, borderRadius: 2, bgcolor: "#f8f9fa" }}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>{col.title}</Typography>
              <Droppable droppableId={col.id}>
                {(provided) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ minHeight: 120 }}>
                    {(columns[col.id] || []).map((c, i) => (
                      <Card key={c.id} card={c} index={i} />
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Paper>
          ))}
        </Box>
      </DragDropContext>

      {/* محرّر (إضافة/تعديل) */}
      <Dialog open={openEditor} onClose={() => setOpenEditor(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id ? "تعديل المهمة" : "إضافة مهمة"}</DialogTitle>
        <DialogContent>
          {uploading && <LinearProgress sx={{ mb: 2 }} />}

          <TextField
            label="العنوان"
            fullWidth sx={{ mb: 2 }}
            value={editing?.title || ""}
            onChange={(e) => setEditing((x) => ({ ...x, title: e.target.value }))}
          />
          <TextField
            label="الوصف"
            fullWidth multiline rows={3} sx={{ mb: 2 }}
            value={editing?.description || ""}
            onChange={(e) => setEditing((x) => ({ ...x, description: e.target.value }))}
          />

          <TextField
            select fullWidth sx={{ mb: 2 }}
            label="العمود"
            value={editing?.status || "todo"}
            onChange={(e) =>
              setEditing((x) => ({ ...x, status: e.target.value, order: 999 /* يعاد ترتيبها لاحقاً */ }))
            }
          >
            {COLUMNS.map(c => <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>)}
          </TextField>

          <TextField
            fullWidth type="date" sx={{ mb: 2 }}
            label="تاريخ الاستحقاق"
            InputLabelProps={{ shrink: true }}
            value={editing?.dueDate || ""}
            onChange={(e) => setEditing((x) => ({ ...x, dueDate: e.target.value }))}
          />

          {/* اللابلز */}
          <Box sx={{ mb: 2 }}>
<Typography variant="subtitle2" sx={{ mb: 0.5 }}><Label sx={{ mr: 0.5 }} /> الوسوم</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {LABELS.map(l => {
                const active = editing?.labels?.includes(l.id);
                return (
                  <Chip
                    key={l.id}
                    label={l.text}
                    onClick={() => {
                      setEditing((x) => {
                        const set = new Set(x.labels || []);
                        active ? set.delete(l.id) : set.add(l.id);
                        return { ...x, labels: [...set] };
                      });
                    }}
                    sx={{ bgcolor: active ? l.color : undefined, color: active ? "#fff" : undefined }}
                    variant={active ? "filled" : "outlined"}
                  />
                );
              })}
            </Box>
          </Box>

          {/* التشك ليست */}
          <Box sx={{ mb: 2 }}>
<Typography variant="subtitle2" sx={{ mb: 0.5 }}>قائمة التحقق</Typography>
            {(editing?.checklist || []).map((it, idx) => (
              <Box key={idx} display="flex" alignItems="center" sx={{ mb: 1, gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!it.done}
                      onChange={(e) => {
                        const list = [...(editing?.checklist || [])];
                        list[idx] = { ...list[idx], done: e.target.checked };
                        setEditing((x) => ({ ...x, checklist: list }));
                      }}
                    />
                  }
                  label={
                    <TextField
                      size="small"
                      value={it.text}
                      onChange={(e) => {
                        const list = [...(editing?.checklist || [])];
                        list[idx] = { ...list[idx], text: e.target.value };
                        setEditing((x) => ({ ...x, checklist: list }));
                      }}
                    />
                  }
                />
                <IconButton color="error" onClick={() => {
                  const list = [...(editing?.checklist || [])];
                  list.splice(idx, 1);
                  setEditing((x) => ({ ...x, checklist: list }));
                }}>
                  <Delete />
                </IconButton>
              </Box>
            ))}
            <Button size="small" onClick={() =>
              setEditing((x) => ({ ...x, checklist: [...(x.checklist || []), { text: "", done: false }] }))
            }>
              + عنصر
            </Button>
          </Box>

          {/* المرفقات */}
          <Box sx={{ mb: 1 }}>
            <Button component="label" startIcon={<AttachFile />}>
              رفع مرفقات
              <input
                hidden
                multiple
                type="file"
                onChange={(e) => {
                  handleUpload(Array.from(e.target.files || []));
                  e.target.value = "";
                }}
              />
            </Button>
            {/* المرفقات المرفوعة: حذف يشيلها من القائمة، وتبديل يرفع الجديد
                (التغيير ينحفظ ويا الكارت بزر الحفظ) */}
            <UploadPreview
              files={editing?.attachments || []}
              disabled={uploading}
              onChange={(list) => {
                const kept = list.filter((x) => !(x instanceof File));
                const added = list.filter((x) => x instanceof File);
                setEditing((e) => ({ ...e, attachments: kept }));
                if (added.length) handleUpload(added);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditor(false)}>إلغاء</Button>
          <Button variant="contained" onClick={saveCard} disabled={uploading}>
            {editing?.id ? "حفظ" : "إضافة"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
